import { Component, OnInit, OnDestroy, Output, EventEmitter } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MsalService } from '@azure/msal-angular';
import { WorkspaceSwitcherService } from '~/core/services/workspace.service';

@Component({
    selector: 'app-workspace-switcher',
    templateUrl: './workspace-switcher.component.html',
    styleUrls: ['./workspace-switcher.component.scss'],
    standalone: false
})
export class WorkspaceSwitcherComponent implements OnInit, OnDestroy {
    
    @Output() closeWorkspaceSwitcher = new EventEmitter<void>();
    
    public availableRoles: string[] = [];
    public allRoles: string[] = ['legal', 'admin'];
    public currentWorkspace: string = '';
    public showAccessDeniedModal: boolean = false;
    public deniedRole: string = '';
    public userName: string = 'User';
    public userInitials: string = 'U';
    public statusText: string = 'Authenticated Session';

    private readonly destroy$ = new Subject<void>();

    constructor(
        private workspaceSwitcherService: WorkspaceSwitcherService,
        private msalService: MsalService
    ) {
        this.extractUserInfo();
    }

    ngOnInit(): void {
        // Subscribe to available roles
        this.workspaceSwitcherService.availableRoles$
            .pipe(takeUntil(this.destroy$))
            .subscribe(roles => {
                console.log('[WorkspaceSwitcher] Available roles updated:', roles);
                this.availableRoles = roles;
            });

        // Subscribe to current workspace
        this.workspaceSwitcherService.currentWorkspace$
            .pipe(takeUntil(this.destroy$))
            .subscribe(workspace => {
                console.log('[WorkspaceSwitcher] Current workspace changed to:', workspace);
                this.currentWorkspace = workspace;
            });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    /**
     * Extract user information from MSAL
     */
    private extractUserInfo(): void {
        try {
            const account = this.msalService.instance.getActiveAccount();
            if (account) {
                // Extract name
                this.userName = account.name || account.username || 'User';
                
                // Extract initials from name
                const nameParts = this.userName.split(' ');
                if (nameParts.length >= 2) {
                    this.userInitials = (nameParts[0][0] + nameParts[nameParts.length - 1][0]).toUpperCase();
                } else if (this.userName.length > 0) {
                    this.userInitials = this.userName.substring(0, 2).toUpperCase();
                }
                
                console.log('[WorkspaceSwitcher] User info extracted:', { name: this.userName, initials: this.userInitials });
            }
        } catch (error) {
            console.error('[WorkspaceSwitcher] Error extracting user info:', error);
        }
    }

    /**
     * Switch to a different workspace
     */
    public switchWorkspace(role: string): void {
        console.log('[WorkspaceSwitcher] switchWorkspace - Attempting to switch to:', role);
        
        const success = this.workspaceSwitcherService.switchWorkspace(role);
        
        if (!success) {
            console.warn('[WorkspaceSwitcher] switchWorkspace - Failed to switch, role not available:', role);
            this.deniedRole = role;
            this.showAccessDeniedModal = true;
        } else {
            // Emit close event on successful workspace switch
            this.closeWorkspaceSwitcher.emit();
        }
    }

    /**
     * Check if role is available
     */
    public isRoleAvailable(role: string): boolean {
        return this.availableRoles.includes(role);
    }

    /**
     * Check if role is current workspace
     */
    public isCurrentWorkspace(role: string): boolean {
        return this.currentWorkspace === role;
    }

    /**
     * Close access denied modal
     */
    public closeAccessDeniedModal(): void {
        this.showAccessDeniedModal = false;
        this.deniedRole = '';
    }

    /**
     * Get display name for role
     */
    public getRoleDisplayName(role: string): string {
        const roleNames: { [key: string]: string } = {
            'admin': 'Admin',
            'legal': 'Legal',
            'developer': 'Developer'
        };
        return roleNames[role] || role.charAt(0).toUpperCase() + role.slice(1);
    }

    /**
     * Get current role display name
     */
    public getCurrentRoleDisplayName(): string {
        if (!this.currentWorkspace) return 'Select a workspace';
        return this.getRoleDisplayName(this.currentWorkspace);
    }

    /**
     * Handle role click (for greyed out roles)
     */
    public handleRoleClick(role: string): void {
        if (!this.isRoleAvailable(role)) {
            console.log('[WorkspaceSwitcher] handleRoleClick - Role not available:', role);
            this.deniedRole = role;
            this.showAccessDeniedModal = true;
            // Auto-hide toast after 3 seconds
            setTimeout(() => {
                this.showAccessDeniedModal = false;
            }, 3000);
        }
    }

    /**
     * Handle sign out
     */
    public handleSignOut(): void {
        console.log('[WorkspaceSwitcher] handleSignOut - User signing out');
        // TODO: Call auth service to sign out
        // window.location.href = '/auth/logout';
    }

    /**
     * Check if workspace switching is enabled (user has multiple roles)
     */
    public isSwitchingEnabled(): boolean {
        return this.workspaceSwitcherService.isSwitchingEnabled();
    }

    /**
     * Check if current workspace is admin-only (user only has admin role)
     */
    public isAdminOnlyWorkspace(): boolean {
        return this.workspaceSwitcherService.isAdminOnlyWorkspace();
    }

    /**
     * Get switching status message
     */
    public getSwitchingStatusMessage(): string {
        if (this.isSwitchingEnabled()) {
            return 'You have access to multiple workspaces. Switch using the options below.';
        } else if (this.isAdminOnlyWorkspace()) {
            return 'You have access to the Admin workspace only. Switching is disabled.';
        } else {
            return 'You have access to one workspace only. Switching is disabled.';
        }
    }
}
